import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Lock, Heart, Eye, EyeOff, Mic, Sparkles, BookOpen, ChevronDown, Play, Pause } from 'lucide-react';
import RecipientJournal from '../components/vault/RecipientJournal';
import { AffirmationCardsView, AffirmationsHeading } from '../components/vault/AffirmationCards';
import { PocketKeepsakeCardView } from '../components/vault/PocketKeepsakeCard';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ── Sent2ment palette (cream / midnight blue / champagne gold) ── */
const C = {
  cream: '#F5F1E8',
  paper: '#FBF8F1',
  midnight: '#0B1F3A',
  gold: '#C6A96B',
  goldSoft: 'rgba(198, 169, 107, 0.12)',
  border: 'rgba(198, 169, 107, 0.22)',
  text: '#0B1F3A',
  textSec: '#5A5A5A',
};

const fadeUp = {
  initial: { y: 18, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1] },
};

const stagger = (delay = 0) => ({
  ...fadeUp,
  transition: { ...fadeUp.transition, delay },
});

/* Journal labels — emotional naming over page numbers */
const JOURNAL_PAGES = [
  { col: 'journal_page_1_message',  label: 'The Beginning',         page: 1 },
  { col: 'journal_page_7_message',  label: 'A gentle moment',     page: 7 },
  { col: 'journal_page_18_message', label: 'A quiet reminder', page: 18 },
  { col: 'journal_page_30_message', label: 'When you need it most',     page: 30 },
];

/* ── Tap-to-reveal card — hidden journal moments + affirmations ── */
const RevealCard = ({ overline, label, body, audioUrl, defaultOpen = false, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasContent = !!(body || audioUrl);
  if (!hasContent) return null;
  return (
    <div
      className="overflow-hidden transition-all duration-500"
      style={{ background: C.paper, border: `1px solid ${C.border}`, borderRadius: '4px' }}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-6 py-5 sm:px-8 sm:py-6 flex items-start justify-between gap-4 hover:bg-[#F5F1E8]/40 transition-colors duration-300"
        aria-expanded={open}
      >
        <div className="flex-1 min-w-0">
          {overline && (
            <p className="text-[10px] tracking-[0.32em] uppercase font-semibold mb-2" style={{ color: C.gold }}>
              {overline}
            </p>
          )}
          <p className="text-lg sm:text-xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
            {label}
          </p>
        </div>
        <ChevronDown
          size={18}
          strokeWidth={1.4}
          className="shrink-0 mt-1 transition-transform duration-500"
          style={{ color: C.gold, transform: open ? 'rotate(180deg)' : 'rotate(0)' }}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-7 sm:px-8 sm:pb-9">
              <div className="w-8 h-px mb-6" style={{ background: `${C.gold}55` }} />
              {audioUrl && (
                <div className="mb-5">
                  <audio controls preload="metadata" src={audioUrl} className="w-full" />
                </div>
              )}
              {body && (
                <p
                  className="text-base sm:text-[17px] leading-[1.85] whitespace-pre-wrap"
                  style={{ color: C.text, fontFamily: 'Georgia, serif' }}
                >
                  {body}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── A calm, intentional voice-message player. Replaces the native <audio>
   controls in the recipient view so we can:
     • own the button label ("Play message" / "Pause")
     • surface gentle supporting copy before / during / after playback
     • avoid the harsh native controls strip
   The underlying playback uses a real <audio> element under the hood — no
   change to storage, source, or behaviour. preload="metadata" is preserved.
   No auto-play, no looping, no distracting animations. */
const VoiceMessagePlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [hasFinished, setHasFinished] = useState(false);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      // Don't auto-play; only play in direct response to a tap.
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      a.pause();
    }
  };

  return (
    <div className="px-1 sm:px-0" data-testid="vault-view-voice-player">
      {/* Pre-play supporting copy. Kept above the button with comfortable
          breathing room so there's a natural pause before interacting. */}
      <p
        className="text-[15px] sm:text-base leading-[1.85] mb-2"
        style={{ color: C.textSec, fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
        data-testid="vault-view-voice-pre-1"
      >
        You might want a quiet moment for this.
      </p>
      <p
        className="text-[15px] sm:text-base leading-[1.85] mb-10"
        style={{ color: C.textSec, fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
        data-testid="vault-view-voice-pre-2"
      >
        Press play whenever you&rsquo;re ready.
      </p>

      <button
        type="button"
        onClick={togglePlay}
        data-testid="vault-view-voice-play-btn"
        aria-label={playing ? 'Pause message' : 'Play message'}
        className="inline-flex items-center gap-3 px-7 py-4 transition-colors duration-300 focus:outline-none"
        style={{
          background: 'transparent',
          border: `1px solid ${C.gold}`,
          color: C.midnight,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          fontSize: '11px',
          fontWeight: 600,
        }}
      >
        {playing ? <Pause size={14} strokeWidth={1.5} /> : <Play size={14} strokeWidth={1.5} />}
        {playing ? 'Pause' : 'Play message'}
      </button>

      {/* Subtle status line — appears only while playing or after first finish.
          Intentionally muted so it never feels dramatic or distracting. */}
      <div className="mt-6 min-h-[1.5em]" aria-live="polite" data-testid="vault-view-voice-status">
        {playing && (
          <p
            className="text-xs italic"
            style={{ color: `${C.gold}CC`, fontFamily: 'Georgia, serif', letterSpacing: '0.04em' }}
            data-testid="vault-view-voice-listening"
          >
            Listening&hellip;
          </p>
        )}
        {!playing && hasFinished && (
          <p
            className="text-xs italic"
            style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}
            data-testid="vault-view-voice-after"
          >
            You can return to this whenever you need.
          </p>
        )}
      </div>

      {/* Hidden underlying <audio>. Kept in the DOM with the original testid so
          existing tests still target it. Native controls deliberately omitted. */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setHasFinished(true); }}
        className="sr-only"
        data-testid="vault-view-audio"
      />
    </div>
  );
};

/* ── Page ── */
const VaultViewerPage = () => {
  const params = useParams();
  const qr_code = params.qr_code || params.qrCode || '';

  const [phase, setPhase] = useState('loading'); // loading | gate | memory | error | notfound | empty
  const [meta, setMeta] = useState(null);
  const [vault, setVault] = useState(null);
  const [error, setError] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [wrongCount, setWrongCount] = useState(0);
  // Remembered ONLY in component memory so the recipient journal can authenticate
  // its own MongoDB-backed save calls. Cleared on hard refresh (the recipient
  // re-enters the passcode then), and never written to localStorage / sessionStorage.
  const [unlockedPwd, setUnlockedPwd] = useState('');

  const fetchVault = useCallback(async (password) => {
    try {
      const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(password ? { password } : {}),
      });
      if (res.status === 401) return { wrongPassword: true };
      if (!res.ok) {
        return { failed: true };
      }
      const data = await res.json();
      return { data };
    } catch {
      return { failed: true };
    }
  }, [qr_code]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/meta`, { cache: 'no-store' });
        if (cancelled) return;
        if (res.status === 404) { setPhase('notfound'); return; }
        if (!res.ok) {
          setError('We couldn’t reach this Memory Vault right now.');
          setPhase('error');
          return;
        }
        const data = await res.json();
        setMeta(data);
        if (!data.vault_enabled) { setPhase('notfound'); return; }

        // Defensive ordering: if a passcode is set we ALWAYS gate the viewer first,
        // before any content fetch — even when the sender hasn't added content yet.
        // This guarantees no path can render protected content without unlock.
        if (data.has_password) {
          setPhase('gate');
          return;
        }
        if (!data.has_content) { setPhase('empty'); return; }

        const r = await fetchVault();
        if (cancelled) return;
        if (r.data) { setVault(r.data); setPhase('memory'); }
        else if (r.failed) { setError('We couldn’t open this Memory Vault right now. Please try again soon.'); setPhase('error'); }
      } catch {
        if (!cancelled) {
          setError('Connection failed. Please check your internet and try again.');
          setPhase('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [qr_code, fetchVault]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setSubmitting(true);
    const r = await fetchVault(pwd);
    if (r.wrongPassword) {
      setWrongCount((c) => c + 1);
      setSubmitting(false);
      return;
    }
    if (r.failed) {
      setError('Something went wrong. Please try again.');
      setPhase('error');
      setSubmitting(false);
      return;
    }
    setVault(r.data);
    setUnlockedPwd(pwd);
    setPhase('memory');
    setSubmitting(false);
  };

  /* ── Loading ── */
  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.cream }} data-testid="vault-viewer-loading">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 animate-spin" size={32} strokeWidth={1.5} style={{ color: C.gold }} />
          <p className="text-sm tracking-[0.15em] uppercase" style={{ color: C.textSec }}>Opening this Memory Vault</p>
        </div>
      </div>
    );
  }

  /* ── Not found / empty ── */
  if (phase === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: C.cream }} data-testid="vault-viewer-notfound">
        <motion.div {...fadeUp} className="max-w-md text-center">
          <div className="w-12 h-12 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: C.goldSoft }}>
            <Heart size={20} strokeWidth={1.5} style={{ color: C.gold }} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
            Nothing here yet
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            We couldn&rsquo;t find a Memory Vault linked to this code.
          </p>
        </motion.div>
      </div>
    );
  }

  // 'empty' (sender hasn't added content yet) is no longer a hard stop. The
  // recipient still gets their full 30-page journal so they can begin writing
  // their own pages while the sender finishes preparing the vault. Falls
  // through into the memory render below with a graceful "being prepared"
  // notice in place of the sender sections.

  /* ── Error ── */
  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: C.cream }} data-testid="vault-viewer-error">
        <motion.div {...fadeUp} className="max-w-md text-center">
          <h1 className="text-2xl sm:text-3xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
            We can't open this right now
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>{error || 'Please try again in a moment.'}</p>
        </motion.div>
      </div>
    );
  }

  /* ── Passcode gate ── */
  if (phase === 'gate') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-20" style={{ background: C.cream }} data-testid="vault-viewer-gate">
        <motion.div {...fadeUp} className="max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-8 flex items-center justify-center" style={{ background: C.goldSoft }}>
            <Lock size={22} strokeWidth={1.5} style={{ color: C.gold }} />
          </div>
          <p className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: C.gold }}>Sent2ment · Memory Vault</p>
          <h1 className="text-2xl sm:text-3xl font-light leading-[1.25] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }} data-testid="vault-gate-heading">
            This Memory Vault was kept just for you
          </h1>
          <p className="text-sm sm:text-base leading-relaxed mb-8 px-2" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            A private space has been gently prepared.<br />
            Please enter the passcode to continue.
          </p>

          <form onSubmit={handleUnlock} className="text-left space-y-4">
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                autoFocus
                autoComplete="current-password"
                placeholder="Passcode"
                data-testid="vault-gate-password-input"
                className="w-full bg-white outline-none px-4 py-3 pr-10 text-sm placeholder:text-[#6B6259]/65 transition-colors duration-300 text-center tracking-[0.15em]"
                style={{ color: C.midnight, border: `1px solid ${C.border}` }}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? 'Hide passcode' : 'Show passcode'}
                className="absolute inset-y-0 right-0 px-3 transition-colors"
                style={{ color: C.textSec }}
              >
                {showPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
              </button>
            </div>

            {wrongCount > 0 && (
              <p className="text-xs text-red-500 text-center" data-testid="vault-gate-wrong">
                That isn't quite right. Please try again.
              </p>
            )}

            {meta?.password_hint && (
              <p className="text-xs italic text-center leading-relaxed" style={{ color: `${C.textSec}CC`, fontFamily: 'Georgia, serif' }} data-testid="vault-gate-hint">
                A gentle reminder: {meta.password_hint}
              </p>
            )}

            <motion.button
              type="submit"
              disabled={!pwd || submitting}
              whileHover={(!pwd || submitting) ? {} : { scale: 1.01 }}
              whileTap={(!pwd || submitting) ? {} : { scale: 0.99 }}
              data-testid="vault-gate-submit-btn"
              className="w-full text-white font-medium tracking-[0.2em] uppercase text-xs py-4 transition-all duration-300 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              style={{ background: C.gold }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={14} /> Opening…</span>
              ) : (
                'Continue'
              )}
            </motion.button>
            <p className="text-center mt-3">
              <a
                href={`/vault-passcode-reset/${encodeURIComponent(qr_code)}`}
                data-testid="vault-viewer-gate-forgot-link"
                className="text-[11px] italic underline"
                style={{ color: `${C.textSec}CC`, fontFamily: 'Georgia, serif' }}
              >
                Forgot your passcode?
              </a>
            </p>
          </form>
        </motion.div>
      </div>
    );
  }

  /* ── Memory ── */
  const v = vault || {};
  const hasVoice = !!v.vault_extra_audio_url;
  const hasPocket = !!(v.pocket_keepsake_audio_url || v.pocket_keepsake_message);
  const affirmations = [1, 2, 3]
    .map((n) => ({
      n,
      audio: v[`affirmation_${n}_audio_url`],
      text: v[`affirmation_message_${n}`],
    }))
    .filter((a) => a.audio || a.text);
  const journal = JOURNAL_PAGES.filter((p) => v[p.col] && String(v[p.col]).trim());

  return (
    <div className="min-h-screen" style={{ background: C.cream, color: C.text }} data-testid="vault-viewer">
      <div className="max-w-2xl mx-auto px-6 sm:px-8 py-24 sm:py-32">

        {/* Intro */}
        <motion.div {...fadeUp} className="text-center mb-16 sm:mb-20">
          <p className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-6" style={{ color: C.gold }}>
            A space kept for you
          </p>
          <h1 className="text-4xl sm:text-[2.75rem] leading-[1.1] font-light mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }} data-testid="vault-viewer-heading">
            Sent2ment Memory Vault
          </h1>
          <div className="w-8 h-px mx-auto" style={{ background: `${C.gold}55` }} />
          <p className="italic text-sm sm:text-base mt-8 leading-relaxed px-4" style={{ color: C.textSec, fontFamily: "'Cormorant Garamond', serif" }}>
            Take your time. Open whatever feels right. Come back as often as you need.
          </p>
        </motion.div>

        {/* 1. Pocket Keepsake — premium pocket card. Always rendered so the
             recipient sees all 3 products as part of their gift. */}
        <motion.section
          {...stagger(0.12)}
          className="mb-14"
          data-testid="vault-view-pocket"
        >
          <PocketKeepsakeCardView
            message={v.pocket_keepsake_message}
            audioUrl={v.pocket_keepsake_audio_url}
          />
        </motion.section>

        {/* 2. Affirmations — tarot-style cards. Always rendered so the
             recipient sees all 3 products as part of their gift. */}
        <motion.section
          {...stagger(0.18)}
          className="mb-14 lg:-mx-24 xl:-mx-40"
          data-testid="vault-view-affirmations"
        >
          <AffirmationsHeading />
          <AffirmationCardsView vault={v} />
        </motion.section>

        {/* 3. Memory Journal — the BookShell book. Sender's hidden messages
            on pages 1/7/18/30 + their voice on page 1 already live inside
            the book; no separate "Hidden journal moments" or standalone
            voice-player section is needed (those would be duplicates).
            Breaks out of the max-w-2xl column to the full viewport so the
            book reads at the same generous width as the sender's preview. */}
        <motion.section
          {...stagger(0.24)}
          className="mb-16 relative"
          style={{
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            width: '100vw',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}
          data-testid="vault-view-journal"
        >
          <RecipientJournal
            qrCode={qr_code}
            password={unlockedPwd}
            senderMessages={{
              1: v.journal_page_1_message || '',
              7: v.journal_page_7_message || '',
              18: v.journal_page_18_message || '',
              30: v.journal_page_30_message || '',
            }}
            senderAudioUrl={v.vault_extra_audio_url}
          />
        </motion.section>

        {/* Footer mark + secure-storage reassurance */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1.2 }}
          className="text-center mt-20"
        >
          <div className="w-8 h-px mx-auto mb-5" style={{ background: `${C.gold}55` }} />
          <p
            className="text-sm italic mb-5 leading-relaxed mx-auto"
            style={{ color: C.textSec, fontFamily: "'Cormorant Garamond', serif", maxWidth: '32em' }}
          >
            Held privately, ready to return to whenever you feel called back.
          </p>
          <div className="mt-10 inline-flex items-center gap-2" style={{ color: `${C.gold}CC` }}>
            <Heart size={13} strokeWidth={1.5} />
            <p className="text-[10px] tracking-[0.4em] uppercase">Securely stored &middot; 10 years</p>
          </div>
          {/* Storage upgrade — soft, non-promotional. Sits at the very bottom
              so it never feels like a sales nudge mid-experience. */}
          <p
            className="text-xs italic mt-8 leading-relaxed mx-auto"
            style={{ color: `${C.textSec}CC`, fontFamily: 'Georgia, serif', maxWidth: '30em' }}
            data-testid="vault-view-storage-extend"
          >
            This memory is securely stored for 10 years. You can extend this anytime.{' '}
            <a
              href="mailto:hello@sent2ment.co.uk?subject=Extend%20my%20memory%20storage"
              className="transition-colors duration-300 hover:opacity-80"
              style={{ color: C.gold, textDecoration: 'underline', textUnderlineOffset: '3px', fontStyle: 'normal' }}
              data-testid="vault-view-storage-extend-link"
            >
              Extend storage
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default VaultViewerPage;
